import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Add,
  Delete,
  ExpandMore,
  ExpandLess,
  DragIndicator,
  SubdirectoryArrowRight,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import colors from "../../Constants/colors";
import {
  AppendixStructure,
  PredefinedSectionDocument,
} from "../../Models/appendixStructure";

interface PredefinedSectionEditModalProps {
  open: boolean;
  onClose: () => void;
  structure: AppendixStructure | null;
  onSave: (sections: PredefinedSectionDocument[], id?: number) => void;
  isSaving?: boolean;
}

let idCounter = 0;
const generateTempId = () => `temp_${++idCounter}_${Date.now()}`;

const deepCloneSections = (
  sections: PredefinedSectionDocument[],
): PredefinedSectionDocument[] => {
  return sections.map((s) => ({
    ...s,
    id: s.id || generateTempId(),
    subsections: s.subsections ? deepCloneSections(s.subsections) : [],
  }));
};

const reorder = (
  sections: PredefinedSectionDocument[],
): PredefinedSectionDocument[] => {
  return sections.map((s, i) => ({
    ...s,
    order: i + 1,
    subsections: s.subsections ? reorder(s.subsections) : [],
  }));
};

interface SectionItemProps {
  section: PredefinedSectionDocument;
  depth: number;
  onUpdate: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onAddSubsection: (parentId: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  isFirst: boolean;
  isLast: boolean;
}

const SectionItem: React.FC<SectionItemProps> = ({
  section,
  depth,
  onUpdate,
  onDelete,
  onAddSubsection,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = section.subsections && section.subsections.length > 0;

  return (
    <Box sx={{ ml: depth * 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          py: 0.5,
          px: 1,
          borderRadius: 1,
          "&:hover": { backgroundColor: colors.gray50 },
        }}
      >
        {depth > 0 && (
          <SubdirectoryArrowRight
            sx={{ fontSize: 16, color: colors.gray400 }}
          />
        )}

        <DragIndicator
          sx={{ fontSize: 18, color: colors.gray400 }}
        />

        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={isFirst}
            onClick={() => onMoveUp(section.id!)}
            sx={{ p: 0.25 }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: isFirst ? colors.gray200 : colors.primary_dark,
              }}
            >
              ▲
            </Typography>
          </IconButton>
          <IconButton
            size="small"
            disabled={isLast}
            onClick={() => onMoveDown(section.id!)}
            sx={{ p: 0.25 }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: isLast ? colors.gray200 : colors.primary_dark,
              }}
            >
              ▼
            </Typography>
          </IconButton>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: colors.primary_dark,
            minWidth: 24,
            fontWeight: 600,
          }}
        >
          {section.order}.
        </Typography>

        <TextField
          size="small"
          value={section.name}
          onChange={(e) => onUpdate(section.id!, e.target.value)}
          variant="outlined"
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: colors.gray200 },
              "&:hover fieldset": { borderColor: colors.accent },
              "&.Mui-focused fieldset": { borderColor: colors.accent },
            },
            "& .MuiInputBase-input": {
              py: 0.75,
              px: 1,
              fontSize: "0.875rem",
              color: colors.primary_dark,
            },
          }}
        />

        <IconButton
          size="small"
          onClick={() => onAddSubsection(section.id!)}
          sx={{ color: colors.accent }}
          title="Add subsection"
        >
          <Add sx={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => onDelete(section.id!)}
          sx={{ color: colors.red }}
          title="Delete section"
        >
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>

        {hasChildren && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: colors.primary_dark }}
          >
            {expanded ? (
              <ExpandLess sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        )}
      </Box>

      {hasChildren && (
        <Collapse in={expanded}>
          {section.subsections!.map((sub, idx) => (
            <SectionItem
              key={sub.id || idx}
              section={sub}
              depth={depth + 1}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAddSubsection={onAddSubsection}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              isFirst={idx === 0}
              isLast={idx === section.subsections!.length - 1}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const PredefinedSectionEditModal: React.FC<PredefinedSectionEditModalProps> = ({
  open,
  onClose,
  structure,
  onSave,
  isSaving = false,
}) => {
  const { t } = useTranslation("workspaces");
  const [sections, setSections] = useState<PredefinedSectionDocument[]>([]);
  const [initialized, setInitialized] = useState(false);

  if (open && !initialized) {
    if (structure && structure.structure.length > 0) {
      setSections(reorder(deepCloneSections(structure.structure)));
    } else {
      setSections([]);
    }
    setInitialized(true);
  }

  if (!open && initialized) {
    setInitialized(false);
  }

  const findAndUpdate = (
    items: PredefinedSectionDocument[],
    id: string,
    updater: (
      item: PredefinedSectionDocument,
    ) => PredefinedSectionDocument | null,
  ): PredefinedSectionDocument[] => {
    const result: PredefinedSectionDocument[] = [];
    for (const item of items) {
      if (item.id === id) {
        const updated = updater(item);
        if (updated) result.push(updated);
      } else {
        result.push({
          ...item,
          subsections: item.subsections
            ? findAndUpdate(item.subsections, id, updater)
            : [],
        });
      }
    }
    return result;
  };

  const handleUpdate = (id: string, name: string) => {
    setSections((prev) =>
      findAndUpdate(prev, id, (item) => ({ ...item, name })),
    );
  };

  const handleDelete = (id: string) => {
    setSections((prev) => reorder(findAndUpdate(prev, id, () => null)));
  };

  const handleAddSubsection = (parentId: string) => {
    setSections((prev) =>
      findAndUpdate(prev, parentId, (item) => ({
        ...item,
        subsections: reorder([
          ...(item.subsections || []),
          {
            id: generateTempId(),
            name: "",
            order: (item.subsections?.length || 0) + 1,
            subsections: [],
          },
        ]),
      })),
    );
  };

  const swapInList = (
    items: PredefinedSectionDocument[],
    id: string,
    direction: "up" | "down",
  ): PredefinedSectionDocument[] => {
    const idx = items.findIndex((s) => s.id === id);
    if (idx !== -1) {
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx >= 0 && swapIdx < items.length) {
        const newItems = [...items];
        [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
        return reorder(newItems);
      }
      return items;
    }
    return items.map((item) => ({
      ...item,
      subsections: item.subsections
        ? swapInList(item.subsections, id, direction)
        : [],
    }));
  };

  const handleMoveUp = (id: string) => {
    setSections((prev) => swapInList(prev, id, "up"));
  };

  const handleMoveDown = (id: string) => {
    setSections((prev) => swapInList(prev, id, "down"));
  };

  const handleAddTopLevel = () => {
    setSections((prev) =>
      reorder([
        ...prev,
        {
          id: generateTempId(),
          name: "",
          order: prev.length + 1,
          subsections: [],
        },
      ]),
    );
  };

  const handleSave = () => {
    const cleanSections = (
      items: PredefinedSectionDocument[],
    ): PredefinedSectionDocument[] => {
      return items
        .filter((item) => item.name.trim() !== "")
        .map((item) => ({
          name: item.name,
          order: item.order,
          subsections: item.subsections
            ? cleanSections(item.subsections)
            : undefined,
        }));
    };

    onSave(cleanSections(sections), structure?.id);
  };

  const isEditing = !!structure;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography
          sx={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: colors.primary_dark,
            borderBottom: `2px solid ${colors.accent}`,
            paddingBottom: "10px",
          }}
        >
          {isEditing
            ? t("editPredefinedSection")
            : t("createPredefinedSection")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {sections.length === 0 ? (
            <Typography
              variant="body1"
              sx={{ color: colors.primary_dark, py: 2, textAlign: "center" }}
            >
              {t("noSectionsYet")}
            </Typography>
          ) : (
            sections.map((section, idx) => (
              <SectionItem
                key={section.id || idx}
                section={section}
                depth={0}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onAddSubsection={handleAddSubsection}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                isFirst={idx === 0}
                isLast={idx === sections.length - 1}
              />
            ))
          )}

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddTopLevel}
            fullWidth
            sx={{
              color: colors.primary_dark,
              borderColor: colors.accent,
              "&:hover": {
                borderColor: colors.primary_dark,
                backgroundColor: colors.gray50,
              },
            }}
          >
            {t("addSection")}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: colors.gray200,
            color: colors.primary_dark,
            "&:hover": {
              backgroundColor: colors.gray400,
            },
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          sx={{
            backgroundColor: colors.accent,
            color: colors.white,
            "&:hover": {
              backgroundColor: colors.primary_dark,
              color: colors.accent,
            },
          }}
        >
          {isSaving ? t("saving") : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PredefinedSectionEditModal;
